import { EyeIcon, PencilIcon, TrashIcon } from "@heroicons/react/outline";
import { useState } from "react";

import { useAuth } from "@/context/auth-context";

import { Column } from "@/components/SettingsTable/types";

import { HUGGINGFACE_PROVIDER } from "@/components/ModelParameters/constants";
import { useProviders } from "@/queries/providers/useProviders";
import { useDeleteProvider } from "@/queries/providers/useDeleteProvider";
import { useQueryClient } from "@tanstack/react-query";
import { CustomProvider } from "@/queries/providers/types";
import { ProviderWithLogo } from "../../ProviderWithLogo";
import {
  ContextMenuItems,
  ContextMenuVariant,
} from "@/components/ui/ContextMenu/types";
import { Action, DropdownMenuVariant } from "@/components/DropdownMenu";

export const useCustomProvidersTable = () => {
  const authContext = useAuth();
  const queryClient = useQueryClient();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [customProvider, setCustomProvider] = useState<CustomProvider | null>(
    null,
  );

  const { mutate: deleteProvider } = useDeleteProvider();

  const {
    data: providers,
    isLoading: isLoadingProviders,
    customProviders,
  } = useProviders({
    userToken: authContext?.userToken ?? "",
  });

  const columns: Column<(typeof providers)[number]>[] = [
    {
      header: "Name",
      content: (provider) => <ProviderWithLogo provider={provider.name} />,
    },
    {
      header: "Client",
      content: (provider) => <ProviderWithLogo provider={provider.client} />,
    },
    { header: "Base URL", content: (provider) => provider.base_url },
  ];

  const actions: Action<(typeof providers)[number]>[] = [
    {
      id: "view",
      label: "View",
      icon: EyeIcon,
      onClick: (provider) => {
        setCustomProvider(provider);
        setIsPreviewModalOpen(true);
      },
    },
    {
      id: "edit",
      label: "Edit",
      icon: PencilIcon,
      onClick: (provider) => {
        setCustomProvider(provider);
        setIsEditModalOpen(true);
      },
      showIf: (model) => model.provider_name !== HUGGINGFACE_PROVIDER,
    },
    {
      id: "delete",
      label: "Delete",
      icon: TrashIcon,
      variant: DropdownMenuVariant.DESTRUCTIVE,
      onClick: (provider) =>
        deleteProvider(provider.id, {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["providers"] });
          },
        }),
    },
  ];

  const contextMenuItems: ContextMenuItems<(typeof providers)[number]>[] = [
    {
      id: "view",
      label: "View provider",
      icon: EyeIcon,
      onClick: (provider) => {
        setCustomProvider(provider);
        setIsPreviewModalOpen(true);
      },
    },
    {
      id: "edit",
      label: "Edit provider",
      icon: PencilIcon,
      onClick: (provider) => {
        setCustomProvider(provider);
        setIsEditModalOpen(true);
      },
      getDisabled: (model) => model.provider_name === HUGGINGFACE_PROVIDER,
      tooltipContent: "HuggingFace providers cannot be edited",
    },
    {
      id: "delete",
      label: "Delete provider",
      icon: TrashIcon,
      onClick: (provider) =>
        deleteProvider(provider.id, {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["providers"] });
          },
        }),
      variant: ContextMenuVariant.DESTRUCTIVE,
    },
  ];

  return {
    columns,
    actions,
    contextMenuItems,
    providers,
    isLoadingProviders,
    customProviders,
    isEditModalOpen,
    setIsEditModalOpen,
    isPreviewModalOpen,
    setIsPreviewModalOpen,
    customProvider,
    setCustomProvider,
  };
};
